import { mockHomeData } from '@/data/mock/home'
import { mockProperties } from '@/data/mock/properties'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { buildPropertiesSearchUrl, cityLabelFromSlug, parseHeroSearchQuery } from '@/lib/parseSearchQuery'

export type SearchSuggestionKind = 'project' | 'property' | 'locality' | 'builder' | 'query' | 'recent'

export interface SearchSuggestion {
  id: string
  title: string
  subtitle?: string
  kind: SearchSuggestionKind
  kindLabel: string
  href: string
}

interface SearchSuggestionOptions {
  query: string
  intent: string
  city: string
  category?: string
  premium?: boolean
  recent?: { label: string; href: string }[]
  limit?: number
  /** Demo/mock inventory — never use while the user is typing in live search. */
  includeMockInventory?: boolean
}

function slugify(value: string) {
  return value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
}

function buildHref(
  intent: string,
  city: string,
  params: Record<string, string | undefined>,
) {
  return buildPropertyHref(
    intent === 'commercial' ? 'commercial' : intent === 'rent' ? 'rent' : intent === 'buy' ? 'buy' : '',
    {
      city: city.toLowerCase(),
      ...params,
    },
  )
}

function tabParams(opts: Pick<SearchSuggestionOptions, 'premium' | 'category'>) {
  return {
    category: opts.category,
  }
}

function popularQueries(opts: Pick<SearchSuggestionOptions, 'intent' | 'premium' | 'category'>) {
  const { intent, premium, category } = opts

  if (category === 'plots') {
    return [
      { label: 'Residential Plots', params: { search: 'Residential Plots', category: 'plots' } },
      { label: 'Commercial Plots', params: { search: 'Commercial Plots', category: 'plots' } },
      { label: 'Agricultural Land', params: { search: 'Agricultural Land', category: 'plots' } },
      { label: 'Farm Land', params: { search: 'Farm Land', category: 'plots' } },
    ]
  }

  if (premium) {
    return [
      { label: 'Premium Projects', params: { premium: 'true' } },
      { label: 'New Launches', params: { search: 'New Launch' } },
      { label: 'Ready to Move', params: { search: 'Ready to Move' } },
      { label: 'Verified Projects', params: { premium: 'true', search: 'Verified' } },
    ]
  }

  if (intent === 'rent') {
    return [
      { label: '2 BHK for Rent', params: { search: '2 BHK', bhk: '2 BHK' } },
      { label: '3 BHK for Rent', params: { search: '3 BHK', bhk: '3 BHK' } },
      { label: 'Furnished Flats', params: { search: 'Furnished' } },
      { label: 'PG / Hostel', params: { search: 'PG' } },
    ]
  }

  if (intent === 'commercial') {
    return [
      { label: 'Office Space', params: { search: 'Office' } },
      { label: 'Shop for Sale', params: { search: 'Shop' } },
      { label: 'Showroom', params: { search: 'Showroom' } },
      { label: 'Warehouse', params: { search: 'Warehouse' } },
    ]
  }

  return [
    { label: '2 BHK', params: { search: '2 BHK', bhk: '2 BHK' } },
    { label: '3 BHK', params: { search: '3 BHK', bhk: '3 BHK' } },
    { label: 'Premium Projects', params: { premium: 'true' } },
    { label: 'Ready to Move', params: { search: 'Ready to Move' } },
  ]
}

function normalize(value: string) {
  return value.trim().toLowerCase()
}

function scoreMatch(text: string, query: string) {
  const hay = normalize(text)
  const needle = normalize(query)
  if (!needle) return 0
  if (hay === needle) return 100
  if (hay.startsWith(needle)) return 80
  if (hay.includes(needle)) return 60
  return 0
}

function uniqueBy<T>(items: T[], keyFn: (item: T) => string) {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = keyFn(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function projectSuggestions(query: string, intent: string, city: string, limit: number) {
  const matches = mockProperties
    .filter((property) => !city || normalize(property.city) === normalize(city))
    .map((property) => {
      const fields = [property.name, property.area, property.location, property.builder, property.bhk]
      const score = Math.max(...fields.map((field) => scoreMatch(String(field), query)))
      return { property, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)

  return uniqueBy(matches, ({ property }) => `${property.name}-${property.area}`)
    .slice(0, limit)
    .map(({ property }) => ({
      id: `project-${property.id}`,
      title: `${property.name} — ${property.area}, ${property.city}`,
      subtitle: property.builder,
      kind: 'project' as const,
      kindLabel: 'Project',
      href: buildHref(intent, city, {
        search: property.name,
        area: slugify(property.area),
        bhk: property.bhk !== 'Office' ? property.bhk : undefined,
      }),
    }))
}

function localitySuggestions(query: string, intent: string, city: string, limit: number) {
  const localities = [
    ...mockHomeData.trendingLocalities.map((item) => ({
      name: item.name,
      city: item.city,
    })),
    ...mockProperties.map((item) => ({ name: item.area, city: item.city })),
  ].filter((item) => !city || normalize(item.city) === normalize(city))

  return uniqueBy(localities, (item) => `${item.name}-${item.city}`)
    .map((item) => ({ item, score: scoreMatch(item.name, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ item }) => ({
      id: `locality-${slugify(item.name)}-${slugify(item.city)}`,
      title: `${item.name}, ${item.city}`,
      kind: 'locality' as const,
      kindLabel: 'Locality',
      href: buildHref(intent, city, {
        search: item.name,
        area: slugify(item.name),
        city: item.city.toLowerCase(),
      }),
    }))
}

function builderSuggestions(query: string, intent: string, city: string, limit: number) {
  return mockHomeData.topBuilders
    .map((builder) => ({ builder, score: scoreMatch(builder.name, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ builder }) => ({
      id: `builder-${builder.id}`,
      title: builder.name,
      subtitle: `${builder.projectCount}+ projects`,
      kind: 'builder' as const,
      kindLabel: 'Builder',
      href: buildHref(intent, city, { search: builder.name }),
    }))
}

function bhkQuerySuggestions(
  query: string,
  intent: string,
  city: string,
  limit: number,
  category?: string,
  premium?: boolean,
) {
  if (intent === 'commercial' || category === 'plots') return []

  const match = query.match(/(\d)\s*bh?k?/i)
  if (!match) return []

  const bhk = `${match[1]} BHK`
  const parsed = parseHeroSearchQuery(query, city, { tabCategory: category })
  const placeLabel = cityLabelFromSlug(parsed.city)

  const suggestions: SearchSuggestion[] = [
    {
      id: `query-${bhk}-${parsed.city}`,
      title: `${bhk} Properties in ${placeLabel}`,
      kind: 'query' as const,
      kindLabel: 'Trending',
      href: buildPropertiesSearchUrl(query, {
        intent,
        defaultCity: city,
        tabCategory: category,
        premium,
      }),
    },
  ]

  if (intent !== 'rent') {
    suggestions.push({
      id: `query-${bhk}-${parsed.city}-rent`,
      title: `${bhk} for Rent in ${placeLabel}`,
      kind: 'query' as const,
      kindLabel: 'Rent',
      href: buildPropertiesSearchUrl(query, {
        intent: 'rent',
        defaultCity: city,
        tabCategory: category,
        premium,
        forceIntent: true,
      }),
    })
  } else {
    suggestions[0] = {
      ...suggestions[0],
      title: `${bhk} for Rent in ${placeLabel}`,
      href: buildPropertiesSearchUrl(query, {
        intent: 'rent',
        defaultCity: city,
        tabCategory: category,
        premium,
        forceIntent: true,
      }),
    }
  }

  return suggestions.slice(0, limit)
}

function defaultSuggestions(
  intent: string,
  city: string,
  recent: SearchSuggestionOptions['recent'],
  opts: Pick<SearchSuggestionOptions, 'premium' | 'category'>,
) {
  const suggestions: SearchSuggestion[] = []

  recent?.slice(0, 3).forEach((item, index) => {
    suggestions.push({
      id: `recent-${index}-${item.href}`,
      title: item.label,
      kind: 'recent',
      kindLabel: 'Recent',
      href: item.href,
    })
  })

  mockHomeData.trendingLocalities
    .filter((item) => !city || normalize(item.city) === normalize(city))
    .slice(0, 4)
    .forEach((item) => {
    suggestions.push({
      id: `trend-${item.id}`,
      title: `${item.name}, ${item.city}`,
      subtitle: item.avgPrice,
      kind: 'locality',
      kindLabel: 'Trending',
      href: buildHref(intent, city, {
        search: item.name,
        area: item.slug,
        city: item.city.toLowerCase(),
        ...tabParams(opts),
      }),
    })
  })

  popularQueries({ intent, ...opts }).forEach((item, index) => {
    suggestions.push({
      id: `popular-${index}`,
      title: `${item.label} in ${city}`,
      kind: 'query',
      kindLabel: 'Popular',
      href: buildHref(intent, city, { ...item.params, ...tabParams(opts) }),
    })
  })

  return suggestions
}

export function getSearchSuggestions({
  query,
  intent,
  city,
  category,
  premium,
  recent = [],
  limit = 10,
  includeMockInventory = false,
}: SearchSuggestionOptions): SearchSuggestion[] {
  const trimmed = query.trim()

  if (!trimmed) {
    return defaultSuggestions(intent, city, recent, { premium, category }).slice(0, limit)
  }

  const suggestions: SearchSuggestion[] = [
    ...bhkQuerySuggestions(trimmed, intent, city, 3, category, premium),
  ]

  if (includeMockInventory) {
    suggestions.push(
      ...projectSuggestions(trimmed, intent, city, 4),
      ...localitySuggestions(trimmed, intent, city, 3),
      ...builderSuggestions(trimmed, intent, city, 2),
    )
  }

  if (suggestions.length === 0) {
    const parsed = parseHeroSearchQuery(trimmed, city, { tabCategory: category, tabIntent: intent })
    const href = buildPropertiesSearchUrl(trimmed, {
      intent,
      defaultCity: city,
      premium,
      tabCategory: category,
    })
    suggestions.push({
      id: `fallback-${trimmed}`,
      title: `Search "${trimmed}" in ${cityLabelFromSlug(parsed.city)}`,
      kind: 'query',
      kindLabel: 'Search',
      href,
    })

    if (parsed.search && parsed.search !== trimmed.toLowerCase()) {
      suggestions.push({
        id: `fallback-parsed-${parsed.search}`,
        title: `${parsed.search} in ${cityLabelFromSlug(parsed.city)}`,
        kind: 'query',
        kindLabel: 'Search',
        href: buildPropertiesSearchUrl(parsed.search, {
          intent,
          defaultCity: city,
          premium,
          tabCategory: parsed.category ?? category,
        }),
      })
    }
  }

  return uniqueBy(suggestions, (item) => item.id).slice(0, limit)
}
